import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { DateInputTR } from "@/components/DateInputTR";
import { formatDateTR, formatDateTimeTR } from "@/lib/dateTR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Receipt, Plus, TrashSimple, PencilSimple, Eye,
  MagnifyingGlass, ClockCounterClockwise, DownloadSimple, Image as ImageIcon,
  FileText, LockKey, Bank, Warning, CaretUp, CaretDown, CaretUpDown,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  pending:  { label: "Bekliyor",    cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved: { label: "Onaylandı",   cls: "bg-[#F7FBE9] text-[#5c8113] border-[#95C11F]" },
  rejected: { label: "Reddedildi",  cls: "bg-red-50 text-red-700 border-red-300" },
};

const SortIndicator = ({ active, dir }) => {
  if (!active) return <CaretUpDown size={10} className="inline ml-1 text-slate-400" />;
  return dir === "asc"
    ? <CaretUp size={10} weight="fill" className="inline ml-1 text-[#1B4776]" />
    : <CaretDown size={10} weight="fill" className="inline ml-1 text-[#1B4776]" />;
};

export default function ExpenseList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user;

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    status: "__all__", category: "__all__", site_id: "__all__",
    start: "", end: "", q: "",
  });

  const [viewing, setViewing] = useState(null);      // full doc

  // Toplu seçim
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sort, setSort] = useState({ key: "date", dir: "desc" });   // Varsayılan: en yeni tarih önce
  const [claimDialog, setClaimDialog] = useState(false);
  const [attachDialog, setAttachDialog] = useState(false);
  const [openClaims, setOpenClaims] = useState([]);
  const [attachTargetId, setAttachTargetId] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [claimForm, setClaimForm] = useState({
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    form_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })(),
    user_iban: user?.iban || "",
    user_position: user?.position || "",
    notes: "",
  });
  const [creatingClaim, setCreatingClaim] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "__all__") params.status = filters.status;
      if (filters.category !== "__all__") params.category = filters.category;
      if (filters.site_id !== "__all__") params.site_id = filters.site_id;
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const { data } = await axios.get(`${API}/expenses`, { params });
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API}/expense-categories`),
          axios.get(`${API}/sites`),
        ]);
        setCategories(c.data || []); setSites(s.data || []);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.category, filters.site_id, filters.start, filters.end]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filters.q) {
      const q = filters.q.toLowerCase();
      list = list.filter(r =>
        (r.description || "").toLowerCase().includes(q) ||
        (r.user_name || "").toLowerCase().includes(q) ||
        (r.category_label || "").toLowerCase().includes(q) ||
        (r.site_name || "").toLowerCase().includes(q)
      );
    }
    // Sıralama
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    const getVal = (r) => {
      switch (key) {
        case "date":            return r.date || "";
        case "user_name":       return (r.user_name || "").toLocaleLowerCase("tr");
        case "site_name":       return (r.site_name || "").toLocaleLowerCase("tr");
        case "category_label":  return (r.category_label || "").toLocaleLowerCase("tr");
        case "amount_gross":    return Number(r.amount_gross || 0);
        case "status":          return r.status || "";
        default:                return "";
      }
    };
    // stable sort: [...list]
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "tr") * dir;
    });
  }, [rows, filters.q, sort]);

  const toggleSort = (key) => {
    setSort(prev => {
      if (prev.key !== key) return { key, dir: key === "amount_gross" || key === "date" ? "desc" : "asc" };
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const totalGross = filtered.reduce((s, r) => s + (r.amount_gross || 0), 0);

  const remove = async (r) => {
    if (!window.confirm(`"${r.description || r.category_label}" masrafını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/expenses/id/${r.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Toplu sil
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} masraf silinsin mi? Talep formuna bağlı olanlar atlanacak.`)) return;
    try {
      const { data } = await axios.post(`${API}/expenses/bulk-delete`, { ids });
      toast.success(`${data.deleted} silindi${data.skipped?.length ? `, ${data.skipped.length} atlandı` : ""}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Talep formu oluştur — seçili masraflardan
  const createClaim = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    // Ön kontrol: hepsi aynı kullanıcı, aynı şantiye ve bağlı olmasın
    const sel = filtered.filter(r => selectedIds.has(r.id));
    const userIds = new Set(sel.map(r => r.user_id));
    const siteIds = new Set(sel.map(r => r.site_id || null));
    if (userIds.size !== 1) { toast.error("Seçilen masraflar aynı personele ait olmalı"); return; }
    if (siteIds.size !== 1) { toast.error("Seçilen masraflar aynı şantiyeye ait olmalı"); return; }
    if (sel.some(r => r.claim_id)) { toast.error("Bir veya daha fazla masraf zaten bir forma bağlı"); return; }

    if (!claimForm.user_iban || claimForm.user_iban.replace(/\s/g, "").length < 16) {
      toast.error("IBAN gereklidir. 26 haneli IBAN'ı girin.");
      return;
    }
    setCreatingClaim(true);
    try {
      const payload = {
        expense_ids: ids,
        period_month: claimForm.period_month,
        period_year: claimForm.period_year,
        form_date: claimForm.form_date,
        user_iban: claimForm.user_iban.replace(/\s/g, ""),
        user_position: claimForm.user_position || "",
        notes: claimForm.notes || "",
      };
      const { data } = await axios.post(`${API}/expense-claims`, payload);
      toast.success(`Form oluşturuldu: ${data.code}`);
      setClaimDialog(false); setSelectedIds(new Set());
      nav(`/masraf/talep/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCreatingClaim(false); }
  };

  // Var olan açık forma ekleme akışı
  const openAttachDialog = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const sel = filtered.filter(r => selectedIds.has(r.id));
    const userIds = new Set(sel.map(r => r.user_id));
    const siteIds = new Set(sel.map(r => r.site_id || null));
    if (userIds.size !== 1) { toast.error("Seçilen masraflar aynı personele ait olmalı"); return; }
    if (siteIds.size !== 1) { toast.error("Seçilen masraflar aynı şantiyeye ait olmalı"); return; }
    if (sel.some(r => r.claim_id)) { toast.error("Bir veya daha fazla masraf zaten bir forma bağlı"); return; }
    const targetUserId = [...userIds][0];
    const targetSiteId = [...siteIds][0];
    try {
      const { data } = await axios.get(`${API}/expense-claims`, {
        params: { status: "draft,submitted" },
      });
      const eligible = (data || []).filter(cl =>
        cl.user_id === targetUserId && (cl.site_id || null) === targetSiteId
      );
      if (eligible.length === 0) {
        toast.error("Uygun (aynı personel + aynı şantiye) açık form bulunamadı");
        return;
      }
      setOpenClaims(eligible);
      setAttachTargetId(eligible[0].id);
      setAttachDialog(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const attachToClaim = async () => {
    if (!attachTargetId) return;
    setAttaching(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await axios.post(
        `${API}/expense-claims/id/${attachTargetId}/attach-expenses`,
        { expense_ids: ids }
      );
      toast.success(`${ids.length} masraf ${data.code} formuna eklendi`);
      setAttachDialog(false); setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAttaching(false); }
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAll = () => {
    const selectableIds = filtered.filter(r => !r.claim_id && !(r.claim_status === "approved"))
                                   .map(r => r.id);
    if (selectableIds.every(id => selectedIds.has(id))) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableIds));
  };

  const exportXlsx = () => {
    const params = new URLSearchParams();
    if (filters.status !== "__all__") params.set("status", filters.status);
    if (filters.category !== "__all__") params.set("category", filters.category);
    if (filters.site_id !== "__all__") params.set("site_id", filters.site_id);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);
    window.open(`${API}/expenses/export.xlsx?${params.toString()}`, "_blank");
  };

  return (
    <Shell
      title="Masraf Listesi"
      subtitle={`Masraf Bildirimi › ${isAdmin ? "Tüm Kayıtlar" : "Kendi Masraflarım"}`}
      hideSiteSelector
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportXlsx} data-testid="expense-export-xlsx"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <DownloadSimple size={14} className="mr-1.5" /> Excel
          </Button>
          <Button onClick={() => nav("/masraf/yeni")} data-testid="expense-new-btn"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
            <Plus size={14} className="mr-1.5" /> Yeni Masraf
          </Button>
        </div>
      }
    >
      {/* Filtre şeridi */}
      <Card className="p-3 rounded-none sm:rounded-sm mb-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <div className="col-span-2 md:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ara</Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <Input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
                   data-testid="expense-filter-q" placeholder="Açıklama / personel / kategori…"
                   className="pl-7 rounded-none sm:rounded-sm h-9" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</Label>
          <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="expense-filter-status" className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tümü</SelectItem>
              <SelectItem value="pending">Bekliyor</SelectItem>
              <SelectItem value="approved">Onaylandı</SelectItem>
              <SelectItem value="rejected">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kategori</Label>
          <Select value={filters.category} onValueChange={v => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="expense-filter-category" className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tümü</SelectItem>
              {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Başlangıç</Label>
          <DateInputTR value={filters.start} onChange={v => setFilters({ ...filters, start: v })}
                       data-testid="expense-filter-start" className="rounded-none sm:rounded-sm mt-1 h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Bitiş</Label>
          <DateInputTR value={filters.end} onChange={v => setFilters({ ...filters, end: v })}
                       data-testid="expense-filter-end" className="rounded-none sm:rounded-sm mt-1 h-9" />
        </div>
      </Card>

      {/* Özet */}
      <Card className="p-3 rounded-none sm:rounded-sm mb-3 flex items-center gap-4 bg-slate-50">
        <Receipt size={22} weight="duotone" className="text-[#95C11F]" />
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Filtrelenen</div>
          <div className="text-lg font-heading font-bold text-[#1B4776]" data-testid="expense-count">{filtered.length} kayıt</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Toplam Brüt</div>
          <div className="text-2xl font-mono font-bold text-[#1B4776]" data-testid="expense-total">
            ₺{totalGross.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
        </div>
      </Card>

      {/* Toplu seçim eylem şeridi */}
      {selectedIds.size > 0 && (
        <Card className="p-3 rounded-none sm:rounded-sm mb-3 bg-[#1B4776] text-white flex items-center gap-3" data-testid="bulk-action-bar">
          <div className="text-sm font-bold">{selectedIds.size} masraf seçildi</div>
          <div className="text-xs opacity-80 ml-2">
            Toplam: ₺{filtered.filter(r => selectedIds.has(r.id))
                              .reduce((s, r) => s + (r.amount_gross || 0), 0)
                              .toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
          </div>
          <div className="ml-auto flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setSelectedIds(new Set())}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-white/10 text-white border-white/30 hover:bg-white/20">
              Seçimi Temizle
            </Button>
            <Button onClick={() => setClaimDialog(true)} data-testid="bulk-create-claim"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#95C11F] hover:bg-[#7fa519] text-white">
              <FileText size={14} className="mr-1.5" /> Talep Formu Oluştur
            </Button>
            <Button onClick={openAttachDialog} data-testid="bulk-attach-claim"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-white text-[#1B4776] border border-white/30 hover:bg-white/90">
              <FileText size={14} className="mr-1.5" /> Var Olan Forma Ekle
            </Button>
            <Button onClick={bulkDelete} data-testid="bulk-delete"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white">
              <TrashSimple size={14} className="mr-1.5" /> Toplu Sil
            </Button>
          </div>
        </Card>
      )}

      {/* Tablo */}
      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <Receipt size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-bold text-slate-700 mb-1">Kayıt yok</div>
          <div className="text-xs text-slate-500">Yeni Masraf butonu ile ilk kaydınızı ekleyin.</div>
        </Card>
      ) : (
        <Card className="rounded-none sm:rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => toggleSort("date")} data-testid="sort-date">
                    Tarih<SortIndicator active={sort.key === "date"} dir={sort.dir} />
                  </th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => toggleSort("user_name")} data-testid="sort-user">
                    Personel<SortIndicator active={sort.key === "user_name"} dir={sort.dir} />
                  </th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => toggleSort("site_name")} data-testid="sort-site">
                    Şantiye<SortIndicator active={sort.key === "site_name"} dir={sort.dir} />
                  </th>
                  <th className="text-left px-3 py-2 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => toggleSort("category_label")} data-testid="sort-category">
                    Kategori<SortIndicator active={sort.key === "category_label"} dir={sort.dir} />
                  </th>
                  <th className="text-left px-3 py-2">Açıklama</th>
                  <th className="text-right px-3 py-2 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => toggleSort("amount_gross")} data-testid="sort-amount">
                    Tutar<SortIndicator active={sort.key === "amount_gross"} dir={sort.dir} />
                  </th>
                  <th className="text-center px-3 py-2 cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => toggleSort("status")} data-testid="sort-status">
                    Durum<SortIndicator active={sort.key === "status"} dir={sort.dir} />
                  </th>
                  <th className="text-right px-3 py-2 pr-4">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                  const isOwner = r.user_id === user?.id;
                  const claimApproved = r.claim_status === "approved";
                  const linked = !!r.claim_id;
                  const canSelect = !linked;
                  const canEdit = ((isOwner && r.status === "pending") || isAdmin) && !claimApproved && !linked;
                  const canDelete = canEdit;
                  return (
                    <tr key={r.id}
                        className={`border-t border-slate-100 hover:bg-slate-50 ${claimApproved ? "bg-[#F7FBE9]/40" : ""}`}
                        data-testid={`expense-row-${i}`}>
                      <td className="px-2 py-2 text-center">
                        {canSelect ? (
                          <Checkbox
                            checked={selectedIds.has(r.id)}
                            onCheckedChange={() => toggleRow(r.id)}
                            data-testid={`expense-select-${i}`}
                          />
                        ) : (
                          <LockKey size={12} className="text-slate-400 mx-auto" />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{formatDateTR(r.date)}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.user_name}
                        {r.on_behalf_of_name && <div className="text-[10px] text-slate-500 italic">→ {r.on_behalf_of_name} adına</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.site_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs font-medium">{r.category_label}</td>
                      <td className="px-3 py-2 text-xs max-w-[260px] truncate" title={r.description}>
                        {r.vendor_name && <div className="font-medium text-slate-800 truncate" title={r.vendor_name}>{r.vendor_name}</div>}
                        <div className={r.vendor_name ? "text-[10px] text-slate-500 truncate" : "text-xs"}>{r.description || (r.vendor_name ? "" : "-")}</div>
                        {r.vendor_tax_number && <div className="text-[9px] font-mono text-slate-400">VKN {r.vendor_tax_number}</div>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-[#1B4776]">
                        ₺{(r.amount_gross || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        <div className="text-[9px] text-slate-400 font-normal">KDV %{r.vat_rate}</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex flex-col items-center gap-1">
                          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${badge.cls}`}>
                            {badge.label}
                          </span>
                          {r.claim_code && (
                            <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold border ${claimApproved ? "bg-[#F7FBE9] text-[#5c8113] border-[#95C11F]" : "bg-blue-50 text-blue-700 border-blue-300"}`}
                                  data-testid={`expense-claim-badge-${i}`}
                                  title={`Talep formu: ${r.claim_code} (${r.claim_status || "draft"})`}>
                              {claimApproved ? <><LockKey size={10} weight="fill" /> Onaylandı</> : <><FileText size={10} /> Forma aktarıldı</>}
                              <span className="font-mono opacity-70 ml-0.5">{r.claim_code}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 pr-4 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setViewing(r)} data-testid={`expense-view-${i}`}
                                  className="h-7 px-2"><Eye size={14} /></Button>
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => nav(`/masraf/duzenle/${r.id}`)} data-testid={`expense-edit-${i}`}
                                    className="h-7 px-2"><PencilSimple size={14} /></Button>
                          )}
                          {canDelete && (
                            <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`expense-delete-${i}`}
                                    className="h-7 px-2 text-red-600 hover:bg-red-50"><TrashSimple size={14} /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detay Dialog */}
      <Dialog open={!!viewing} onOpenChange={o => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>Masraf Detayı</DialogTitle>
            <DialogDescription className="text-xs">
              {formatDateTR(viewing?.date)} · {viewing?.user_name}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500 text-xs">Kategori:</span> <b>{viewing.category_label}</b></div>
                <div><span className="text-slate-500 text-xs">Şantiye:</span> {viewing.site_name || "—"}</div>
                <div><span className="text-slate-500 text-xs">Ödeme:</span> {viewing.payment_method_label}</div>
                <div><span className="text-slate-500 text-xs">Durum:</span>
                  <span className={`ml-1 inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${STATUS_BADGE[viewing.status]?.cls}`}>
                    {STATUS_BADGE[viewing.status]?.label}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2 border border-slate-200">
                <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Net</div>
                     <div className="font-mono font-bold">₺{(viewing.net_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-slate-500">KDV %{viewing.vat_rate}</div>
                     <div className="font-mono font-bold text-amber-700">₺{(viewing.vat_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-slate-500">Brüt</div>
                     <div className="font-mono font-bold text-[#1B4776]">₺{(viewing.amount_gross || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</div></div>
              </div>
              {(viewing.vendor_name || viewing.vendor_tax_number) && (
                <div className="bg-slate-50 border-l-4 border-[#1B4776] p-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-0.5">Fatura Firma Bilgileri</div>
                  {viewing.vendor_name && <div className="text-sm font-bold text-slate-800" data-testid="detail-vendor-name">{viewing.vendor_name}</div>}
                  {viewing.vendor_tax_number && <div className="text-xs text-slate-600 font-mono" data-testid="detail-vendor-tax">VKN: {viewing.vendor_tax_number}</div>}
                </div>
              )}
              {viewing.description && (
                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Açıklama</div>
                     <div className="text-sm">{viewing.description}</div></div>
              )}
              {viewing.rejection_reason && (
                <div className="bg-red-50 border-l-4 border-red-400 p-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">Red Gerekçesi</div>
                  <div className="text-sm text-red-800">{viewing.rejection_reason}</div>
                </div>
              )}
              {viewing.approved_by_name && (
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <ClockCounterClockwise size={12} />
                  {viewing.status === "approved" ? "Onaylayan:" : "İşlem yapan:"} <b>{viewing.approved_by_name}</b>
                  {viewing.approved_at && <span> · {formatDateTimeTR(viewing.approved_at)}</span>}
                </div>
              )}
              {viewing.invoice_file_path && (
                <div>
                  <a href={`${API}/files/${viewing.invoice_file_path}`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-[#1B4776] hover:underline">
                    <ImageIcon size={14} /> Fatura görselini aç
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Var Olan Forma Ekle Dialog */}
      <Dialog open={attachDialog} onOpenChange={o => !o && setAttachDialog(false)}>
        <DialogContent className="max-w-md rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>Var Olan Forma Ekle</DialogTitle>
            <DialogDescription className="text-xs">
              {selectedIds.size} masraf, seçtiğiniz **onaylanmamış** talep formuna eklenecek.
              Aynı personel + aynı şantiye kısıtı uygulanır.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Talep Formu</Label>
            <Select value={attachTargetId} onValueChange={setAttachTargetId}>
              <SelectTrigger data-testid="attach-target-select" className="rounded-none sm:rounded-sm h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {openClaims.map(cl => {
                  const statusLbl = cl.status === "submitted" ? "Beklemede" : "Taslak";
                  return (
                    <SelectItem key={cl.id} value={cl.id}>
                      <span className="font-mono">{cl.code}</span>
                      <span className="text-slate-500"> · {statusLbl}</span>
                      <span className="text-slate-500"> · ₺{Number(cl.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachDialog(false)}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Vazgeç</Button>
            <Button onClick={attachToClaim} disabled={attaching || !attachTargetId} data-testid="attach-confirm"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
              {attaching ? "…" : "Forma Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talep Formu Oluştur Dialog */}
      <Dialog open={claimDialog} onOpenChange={o => !o && setClaimDialog(false)}>
        <DialogContent className="max-w-lg rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>Masraf Talep Formu Oluştur</DialogTitle>
            <DialogDescription className="text-xs">
              {selectedIds.size} masraftan yeni bir talep formu düzenlenecek.
              Formun numarası proje koduna göre otomatik oluşturulur (MSR-XXX-000N).
            </DialogDescription>
          </DialogHeader>

          <Card className="p-3 border-l-4 border-amber-500 bg-amber-50 flex items-start gap-2 mb-2 rounded-none sm:rounded-sm">
            <Warning size={18} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              <b>IBAN gereklidir.</b> Bu bilgi formda ve PDF çıktısında yer alacak, profilinize kaydedilecek.
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Form Tarihi *</Label>
              <DateInputTR value={claimForm.form_date} onChange={v => setClaimForm({ ...claimForm, form_date: v })}
                           data-testid="claim-new-date" className="rounded-none sm:rounded-sm h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Görev</Label>
              <Input value={claimForm.user_position}
                     onChange={e => setClaimForm({ ...claimForm, user_position: e.target.value })}
                     data-testid="claim-new-position" placeholder="Örn: Şantiye Şefi"
                     className="rounded-none sm:rounded-sm h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Dönem Ayı</Label>
              <Select value={String(claimForm.period_month)}
                      onValueChange={v => setClaimForm({ ...claimForm, period_month: Number(v) })}>
                <SelectTrigger className="rounded-none sm:rounded-sm h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"].map((m, i) =>
                    <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Dönem Yılı</Label>
              <Input type="number" value={claimForm.period_year}
                     onChange={e => setClaimForm({ ...claimForm, period_year: Number(e.target.value) })}
                     className="rounded-none sm:rounded-sm h-9 mt-1 font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                <Bank size={11} /> IBAN * (26 hane)
              </Label>
              <Input value={claimForm.user_iban}
                     onChange={e => setClaimForm({ ...claimForm, user_iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 26) })}
                     data-testid="claim-new-iban"
                     placeholder="TR330006100519786457841326"
                     maxLength={26}
                     className="rounded-none sm:rounded-sm h-9 mt-1 font-mono" />
              <div className="text-[10px] text-slate-500 mt-0.5">Girdiğiniz IBAN profilinize kaydedilir, sonraki formlarda otomatik dolar.</div>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar (opsiyonel)</Label>
              <Textarea rows={2} value={claimForm.notes}
                        onChange={e => setClaimForm({ ...claimForm, notes: e.target.value })}
                        className="rounded-none sm:rounded-sm mt-1" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimDialog(false)}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Vazgeç</Button>
            <Button onClick={createClaim} disabled={creatingClaim} data-testid="claim-new-confirm"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
              {creatingClaim ? "…" : "Formu Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
